import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import {
  deletePendingPayment,
  listPendingPayments,
  updatePendingPayment,
  createPendingPayment,
} from '../services/pendingPaymentsService';
import { PendingPayment, PendingPaymentStatus } from '../types/pendingPayment';
import { formatDate } from '../utils/format';

const statusOptions: Array<{ value: PendingPaymentStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'received', label: 'Received' },
];

const statusLabels: Record<PendingPaymentStatus, string> = {
  pending: 'Pending',
  overdue: 'Overdue',
  received: 'Received',
};

const statusBadgeStyles: Record<PendingPaymentStatus, { background: string; color: string }> = {
  pending: { background: 'rgba(250, 204, 21, 0.16)', color: '#ca8a04' },
  overdue: { background: 'rgba(239, 68, 68, 0.16)', color: '#dc2626' },
  received: { background: 'rgba(34, 197, 94, 0.16)', color: '#15803d' },
};

const formatCurrency = (amount: string | number) =>
  `₹ ${Number(amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// date formatting centralized in utils/format

export const PendingPaymentsPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<PendingPaymentStatus | 'all'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; paymentId: number | null }>({
    isOpen: false,
    paymentId: null,
  });
  const [createForm, setCreateForm] = useState<{
    client_name: string;
    amount: string;
    due_date: string;
    status: PendingPaymentStatus;
    notes: string;
  }>({
    client_name: '',
    amount: '',
    due_date: '',
    status: 'pending',
    notes: '',
  });

  const loadPayments = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await listPendingPayments(token, { status: statusFilter });
      setPayments(response.data);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load pending payments.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, token]);

  const summary = useMemo(() => {
    const base = {
      pendingAmount: 0,
      overdueAmount: 0,
      receivedAmount: 0,
      overdueCount: 0,
      pendingCount: 0,
      receivedCount: 0,
    };

    return payments.reduce((acc, payment) => {
      const amount = Number(payment.amount ?? 0);
      if (payment.status === 'pending') {
        acc.pendingAmount += amount;
        acc.pendingCount += 1;
      } else if (payment.status === 'overdue') {
        acc.overdueAmount += amount;
        acc.overdueCount += 1;
      } else if (payment.status === 'received') {
        acc.receivedAmount += amount;
        acc.receivedCount += 1;
      }
      return acc;
    }, base);
  }, [payments]);

  const handleStatusUpdate = async (payment: PendingPayment, nextStatus: PendingPaymentStatus) => {
    if (!token) return;
    setActionLoading(payment.id);

    const payload: Partial<PendingPayment> = {
      status: nextStatus,
      received_on: nextStatus === 'received' ? new Date().toISOString().slice(0, 10) : null,
    };

    try {
      await updatePendingPayment(token, payment.id, payload);
      showToast(`Marked ${payment.client_name} as ${statusLabels[nextStatus].toLowerCase()}.`, 'success');
      await loadPayments();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Unable to update payment status.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (payment: PendingPayment) => {
    setDeleteConfirm({ isOpen: true, paymentId: payment.id });
  };

  const renderStatusBadge = (status: PendingPaymentStatus) => {
    const styles = statusBadgeStyles[status];
    return (
      <span
        className="badge"
        style={{
          background: styles.background,
          color: styles.color,
          fontWeight: 600,
        }}
      >
        {statusLabels[status]}
      </span>
    );
  };

  return (
    <AppLayout
      title="Pending Payments"
      toolbar={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'nowrap' }}>
          <label className="form-label" style={{ marginBottom: 0 }}>
            Status
          </label>
          <select
            className="input"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as PendingPaymentStatus | 'all')}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            style={{ width: 'auto', padding: '10px 18px' }}
            onClick={() => setCreateOpen(true)}
          >
            + Add pending payment
          </button>
        </div>
      }
    >

      <section className="cards">
        <div className="card">
          <h3>Pending amount</h3>
          <p style={{ fontSize: '1.6rem', fontWeight: 600, margin: '6px 0' }}>
            {formatCurrency(summary.pendingAmount)}
          </p>
          <span className="subtle-text">{summary.pendingCount} open payments</span>
        </div>
        <div className="card">
          <h3>Overdue</h3>
          <p style={{ fontSize: '1.6rem', fontWeight: 600, margin: '6px 0', color: '#dc2626' }}>
            {formatCurrency(summary.overdueAmount)}
          </p>
          <span className="subtle-text">{summary.overdueCount} overdue payments</span>
        </div>
        <div className="card">
          <h3>Received</h3>
          <p style={{ fontSize: '1.6rem', fontWeight: 600, margin: '6px 0', color: '#15803d' }}>
            {formatCurrency(summary.receivedAmount)}
          </p>
          <span className="subtle-text">{summary.receivedCount} marked as received</span>
        </div>
      </section>

      {createOpen && (
        <div className="modal-overlay" onClick={() => setCreateOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h3>Add pending payment</h3>
              <button type="button" className="modal-close" onClick={() => setCreateOpen(false)} aria-label="Close">
                ×
              </button>
            </header>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">Client name</label>
                <input
                  className="input"
                  value={createForm.client_name}
                  onChange={(e) => setCreateForm((p) => ({ ...p, client_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm((p) => ({ ...p, amount: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due date</label>
                <input
                  type="date"
                  className="input"
                  value={createForm.due_date}
                  onChange={(e) => setCreateForm((p) => ({ ...p, due_date: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="input"
                  value={createForm.status}
                  onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value as PendingPaymentStatus }))}
                >
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                  <option value="received">Received</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input
                  className="input"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn btn-secondary" style={{ width: 'auto', marginTop: 0 }} onClick={() => setCreateOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ width: 'auto' }}
                  onClick={async () => {
                    if (!token) return;
                    try {
                      await createPendingPayment(token, {
                        client_name: createForm.client_name.trim(),
                        amount: Number(createForm.amount || 0),
                        due_date: createForm.due_date || null,
                        status: createForm.status,
                        notes: createForm.notes || null,
                      });
                      setCreateOpen(false);
                      setCreateForm({ client_name: '', amount: '', due_date: '', status: 'pending', notes: '' });
                      await loadPayments();
                      showToast('Pending payment added successfully.', 'success');
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : 'Unable to add pending payment.', 'error');
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <section className="card" style={{ padding: 0 }}>
        <div className="table">
          <div className="table-head" style={{ gridTemplateColumns: '1.4fr 140px 140px 140px 1.4fr 1fr 1fr' }}>
            <span>Client</span>
            <span>Amount (₹)</span>
            <span>Due date</span>
            <span>Status</span>
            <span>Notes</span>
            <span>Updated</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="table-row" style={{ gridTemplateColumns: '1.4fr 140px 140px 140px 1.4fr 1fr 1fr', alignItems: 'center' }}>
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                </div>
              ))}
            </>
          ) : payments.length === 0 ? (
            <div className="table-row" style={{ gridTemplateColumns: '1fr', padding: '40px 20px' }}>
              <span style={{ textAlign: 'center', color: 'var(--neutral-600)' }}>
                No pending payments found for the selected filter.
              </span>
            </div>
          ) : (
            payments.map((payment) => (
              <div
                className="table-row"
                key={payment.id}
                style={{ gridTemplateColumns: '1.4fr 140px 140px 140px 1.4fr 1fr 1fr', alignItems: 'center' }}
              >
                <span style={{ fontWeight: 600 }}>{payment.client_name}</span>
                <span>{Number(payment.amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>{formatDate(payment.due_date)}</span>
                <span>{renderStatusBadge(payment.status)}</span>
                <span>{payment.notes || '—'}</span>
                <span>{formatDate(payment.updated_at)}</span>
                <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  {payment.status !== 'received' && (
                    <button
                      type="button"
                      className="btn"
                      style={{
                        width: 'auto',
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        background: 'linear-gradient(135deg, #0b5c2c, #16a34a)',
                      }}
                      disabled={actionLoading === payment.id}
                      onClick={() => handleStatusUpdate(payment, 'received')}
                      aria-label="Mark received"
                    >
                      {actionLoading === payment.id ? '…' : '✓'}
                    </button>
                  )}
                  {payment.status === 'pending' && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: 'auto', padding: '6px 12px', fontSize: '0.95rem' }}
                      disabled={actionLoading === payment.id}
                      onClick={() => handleStatusUpdate(payment, 'overdue')}
                      aria-label="Mark overdue"
                    >
                      ⏰
                    </button>
                  )}
                  {payment.status !== 'pending' && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: 'auto', padding: '6px 12px', fontSize: '0.95rem' }}
                      disabled={actionLoading === payment.id}
                      onClick={() => handleStatusUpdate(payment, 'pending')}
                      aria-label="Move to pending"
                    >
                      ↺
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      width: 'auto',
                      padding: '6px 12px',
                      fontSize: '0.95rem',
                      color: '#dc2626',
                      borderColor: 'rgba(239,68,68,0.24)',
                    }}
                    disabled={actionLoading === payment.id}
                    onClick={() => handleDelete(payment)}
                    aria-label="Delete"
                  >
                    🗑
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      {deleteConfirm.isOpen && (
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title="Delete Pending Payment"
          message={`Are you sure you want to delete the pending payment for ${deleteConfirm.paymentId ? payments.find(p => p.id === deleteConfirm.paymentId)?.client_name || 'this client' : 'this client'}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={async () => {
            if (!token || !deleteConfirm.paymentId) return;
            setActionLoading(deleteConfirm.paymentId);
            try {
              await deletePendingPayment(token, deleteConfirm.paymentId);
              showToast('Pending payment removed successfully.', 'success');
              setDeleteConfirm({ isOpen: false, paymentId: null });
              await loadPayments();
            } catch (err) {
              showToast(err instanceof Error ? err.message : 'Unable to delete pending payment.', 'error');
              setDeleteConfirm({ isOpen: false, paymentId: null });
            } finally {
              setActionLoading(null);
            }
          }}
          onCancel={() => setDeleteConfirm({ isOpen: false, paymentId: null })}
        />
      )}
    </AppLayout>
  );
};

